import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";

const Contact = () => {
  // Create a reference for the form element
     useEffect(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    }, []);
    
  const navigate = useNavigate();
  const formRef = useRef();
  const [loading, setLoading] = useState(false); // For handling loading state
  const [submitStatus, setSubmitStatus] = useState(""); // For handling submit status message

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      phone: "", // Added phone field
      subject: "",
      message: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      email: Yup.string()
        .email("Invalid email format")
        .required("Email is required"),
      phone: Yup.string().required("Phone number is required"), // Added validation for phone
      subject: Yup.string().required("Subject is required"),
      message: Yup.string().required("Message is required"),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setSubmitStatus("Sending message...");

        // Create FormData and append fields
        let formData = await new FormData();
        await formData.append("name", values.name);
        await formData.append("email", values.email);
        await formData.append("phone", values.phone);
        await formData.append("subject", values.subject);
        await formData.append("message", values.message);

        // Send fetch request with FormData directly (no JSON.stringify)
        const response = await fetch(
          "https://script.google.com/macros/s/AKfycbwU-2Q9YFOVDVU-UhRGZA8slWlE6Ct6rtOXU1TOPtUZjo7KlLZo3agHFe6yoeuED7TI/exec",
          {
            method: "POST",
            body: formData,
          }
        );

        if (response.status === 200) {
          setLoading(false);
          setSubmitStatus("Submitted...");
          navigate("/thankyou");
        } else {
          setLoading(false);
          setSubmitStatus("Failed to submit, please try again...");
        }
      } catch (err) {
        setLoading(false);
        setSubmitStatus("Failed to submit, please try again...");
      }
    },
  });

  return (
    <>
      <Helmet>
        <title>Contact Us - ANS Safety Nets</title>
        <meta
          name="description"
          content="Get in touch with ANS Safety Nets, a leading provider of high-quality safety nets and solutions in Bengaluru. We specialize in balcony safety nets, children safety nets, industrial safety nets, pets safety nets, and more."
        />
        <meta
          name="keywords"
          content="safety nets in bengaluru , ans safety nets ,About ANS Safety Nets, Safety Nets in Bengaluru, ANS Safety Nets, safety nets in bengaluru, Safety Solutions, Bengaluru Safety Nets , balcony safety nets in bengaluru , balcony safety nets,pegion safety nets in bengaluru, monkey safety nets in bengaluru,Ducteria Nets in bengaluru, Pets Safety Nets in bengaluru, Industrial Safety Nets in bengaluru, Children Safety Nets in bengaluru, Bird Spikes in , Cricket Practice Nets in bengaluru"
        />
        <meta name="author" content="ANS Safety Nets, Bengaluru" />
        <link rel="canonical" href="https://www.anssafetynets.com/contact" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "Contact Us - ANS Safety Nets",
            description:
              "Get in touch with ANS Safety Nets, a leading provider of high-quality safety nets and solutions in Bengaluru. We specialize in balcony safety nets, children safety nets, industrial safety nets, pets safety nets, and more.",
            keywords: [
              "safety nets in bengaluru",
              "ans safety nets",
              "About ANS Safety Nets",
              "Safety Nets in Bengaluru",
              "ANS Safety Nets",
              "Safety Solutions",
              "Bengaluru Safety Nets",
              "balcony safety nets in bengaluru",
              "balcony safety nets",
              "pegion safety nets in bengaluru",
              "monkey safety nets in bengaluru",
              "Ducteria Nets in bengaluru",
              "Pets Safety Nets in bengaluru",
              "Industrial Safety Nets in bengaluru",
              "Children Safety Nets in bengaluru",
              "Bird Spikes",
              "Cricket Practice Nets in bengaluru",
            ],
            contactPoint: {
              "@type": "ContactPoint",
              telephone: "+91-7416116587",
              contactType: "Customer Service",
              areaServed: "IN",
              availableLanguage: ["en", "hi", "kn"],
            },
            url: "https://www.anssafetynets.com/contact",
          })}
        </script>
      </Helmet>
      <div className="pt-16">
        <div className="bg-[#2c3639] py-20 text-center text-white">
          <h1 className="text-4xl font-bold mb-4">Contact Us</h1>
          <i className="text-xl">Get in touch with our team</i>
        </div>

        <div className="py-20 max-w-7xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-12">
          <div>
            <h2 className="text-2xl font-bold mb-6">Get in Touch</h2>
            <p className="text-gray-600">+91 7416116587</p>
            <p className="text-gray-600">anssafetynets03@gmail.com</p>
            <p className="text-black underline my-[5px]">Location</p>
            <p className="text-gray-600">
              74/9, 5th Cross Rd, Muthyala Nagar, Bahubali Nagar, Jalahalli,
              Bengaluru, Karnataka, 560013
            </p>
          </div>

          <div className="bg-white p-8 rounded-lg shadow-md">
            <h2 className="text-2xl font-bold mb-6">Send us a Message</h2>
            <form
              onSubmit={formik.handleSubmit}
              ref={formRef} // Attach the reference here
              className="space-y-4"
            >
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700"
                >
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  className="mt-1  block w-full rounded-md border-black border-[1px] shadow-sm p-3 focus:ring-2 focus:ring-blue-500 focus:border-[#2c3639]"
                  required
                />
                {formik.errors.name && formik.touched.name && (
                  <p className="text-red-500 text-sm">{formik.errors.name}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  className="mt-1  block w-full rounded-md border-black border-[1px] shadow-sm p-3 focus:ring-2 focus:ring-blue-500 focus:border-[#2c3639]"
                  required
                />
                {formik.errors.email && formik.touched.email && (
                  <p className="text-red-500 text-sm">{formik.errors.email}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-gray-700"
                >
                  Phone
                </label>
                <input
                  type="text"
                  id="phone"
                  name="phone"
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  className="mt-1  block w-full rounded-md border-black border-[1px] shadow-sm p-3 focus:ring-2 focus:ring-blue-500 focus:border-[#2c3639]"
                  required
                />
                {formik.errors.phone && formik.touched.phone && (
                  <p className="text-red-500 text-sm">{formik.errors.phone}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="subject"
                  className="block text-sm font-medium text-gray-700"
                >
                  Subject
                </label>
                <select
                  id="subject"
                  name="subject"
                  value={formik.values.subject}
                  onChange={formik.handleChange}
                  className="mt-1  block w-full rounded-md border-black border-[1px] shadow-sm p-3 focus:ring-2 focus:ring-blue-500 focus:border-[#2c3639]"
                  required
                >
                  <option value="">Select a subject</option>
                  <option value="quote">Request Quote</option>
                  <option value="support">Technical Support</option>
                  <option value="sales">Sales Inquiry</option>
                  <option value="other">Other</option>
                </select>
                {formik.errors.subject && formik.touched.subject && (
                  <p className="text-red-500 text-sm">
                    {formik.errors.subject}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700"
                >
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formik.values.message}
                  onChange={formik.handleChange}
                  className="mt-1  block w-full rounded-md border-black border-[1px] shadow-sm p-3 focus:ring-2 focus:ring-blue-500 focus:border-[#2c3639]"
                  required
                ></textarea>
                {formik.errors.message && formik.touched.message && (
                  <p className="text-red-500 text-sm">
                    {formik.errors.message}
                  </p>
                )}
              </div>

              <button
                type="submit"
                className="homecontactnavigatebtn w-full text-black"
                disabled={loading} // Disable button while loading
              >
                {loading ? "Sending..." : submitStatus || "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;
